/**
 * WAT-10279: Shared utility for modal priority resolution.
 * Used by both App Router (modal-orchestrator.client.tsx) and
 * Pages Router (pages/index.tsx getServerSideProps).
 */

/** Valid modal types for orchestrator priority resolution */
export type ModalType = "EXITMODALV2" | "REVISITORMODAL" | "MARKETINGAIMODAL";

/**
 * WAT-10279: Canonical set of all orchestrated modal types.
 * Used to strip modal components from the canvas when priority mode is active.
 * Single source of truth — imported by all 4 page entry points.
 */
export const ORCHESTRATED_MODAL_TYPES: ReadonlySet<string> = new Set<ModalType>([
  "EXITMODALV2",
  "REVISITORMODAL",
  "MARKETINGAIMODAL"
]);

/**
 * WAT-10279: Safely resolve a component's type string.
 *
 * The Storyblok page adapter is inconsistent — regular components
 * (VERTICALSPACER, OFFERCARDS …) expose `type` at the top level,
 * while modal components (EXITMODALV2, REVISITORMODAL, MARKETINGAIMODAL)
 * only carry `type` inside `props`.  This helper normalises the lookup
 * so every consumer gets the correct value regardless of shape.
 */
export const getComponentType = (c: any): string | undefined => c?.type || c?.props?.type;

interface ModalPriorityEntry {
  priority: string;
  modalType: string;
}

/**
 * Sort modalPriorityMap by ascending priority (1 = highest) and return
 * the ordered list of modal types. CMS authors assign numeric priorities;
 * this util normalises the string values and sorts them.
 */
export function getSortedModalPriorityList(
  modalPriorityMap?: ModalPriorityEntry[]
): ModalType[] {
  if (!modalPriorityMap?.length) {
    return [];
  }
  return [...modalPriorityMap]
    .sort(
      (a, b) => parseInt(a.priority, 10) - parseInt(b.priority, 10)
    )
    .map(entry => entry.modalType)
    .filter(Boolean) as ModalType[];
}
